import React, { useState } from 'react';
import { Download, ChevronDown, Square, Loader2, Sparkles, CheckCircle2, XCircle, RotateCcw, Clock } from 'lucide-react';
import { toast } from 'sonner';
import './lead-runs.css';

const runTitle = (r) => r.city ? `Bulk Deep Analysis — ${r.industries.join(', ')} · ${r.city}`
  : r.count === 1 ? `Deep Analysis — ${r.first_name || '1 business'}` : `Bulk Deep Analysis — ${r.count} businesses`;

export const RunRow = ({ run: r, api, workspaceId, theme }) => {
  const { textPrimary, textSecondary } = theme;
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const base = `/api/workspaces/${workspaceId}/lead-runs`;
  const active = ['queued', 'running', 'authorizing'].includes(r.status);
  const { ready, failed, cancelled } = r.counts;
  const total = r.found || r.count;
  const allFailed = !active && failed > 0 && ready === 0;
  const fetchItems = async () => { const { data } = await api.get(`${base}/${r.run_id}`); setItems(data.items); return data; };
  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setLoading(true);
    try { await fetchItems(); setOpen(true); } catch { toast.error('Could not load details'); } finally { setLoading(false); }
  };
  const cancel = async () => {
    try { await api.post(`${base}/${r.run_id}/cancel`); toast.success('Cancelling — unused funds will be released'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not cancel'); }
  };
  const retry = async () => {
    try {
      const { items: list } = await fetchItems();
      const ids = list.filter(i => i.status === 'failed' && i.retryable).map(i => i.lead_id);
      if (!ids.length) { toast.info('Nothing left to retry — these businesses were already re-run or removed.'); return; }
      window.dispatchEvent(new CustomEvent('voxinity:lead-package-request', { detail: { lead_ids: ids } }));
    } catch { toast.error('Could not load failed businesses'); }
  };
  const download = async () => {
    try {
      const { run, items: list } = await fetchItems();
      const cell = v => `"${String(v ?? '').replace(/^[=+@-]/, "'$&").replace(/"/g, '""')}"`;
      const rows = [['Receipt', r.run_id], ['Currency', 'USD'], ['Rate', run.rate], ['Charged', run.charged], ['Released', run.released],
        [], ['Business', 'Status', 'Stage', 'Charge USD', 'Error'],
        ...list.map(i => [i.lead.name, i.status, i.stage, i.status === 'ready' && !run.unlimited ? run.rate : 0, i.error || ''])];
      const url = URL.createObjectURL(new Blob([rows.map(x => x.map(cell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a'); a.href = url; a.download = `deep-analysis-receipt-${r.run_id}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Could not download receipt'); }
  };
  const status = r.status === 'queued' ? 'Queued' : active ? `Running · ${ready + failed} of ${total} done`
    : r.status === 'cancelled' ? 'Cancelled' : allFailed ? 'Failed' : failed ? `Done · ${failed} failed` : 'Done';
  const btn = `p-1.5 rounded-lg ${textSecondary} hover:text-teal-400 transition-colors`;
  return <div className="px-4 py-3" data-testid={`lead-run-${r.run_id}`}>
    <div className="flex flex-wrap sm:flex-nowrap items-center gap-3">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-violet-400/10 text-violet-400"><Sparkles size={15} /></div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium truncate ${textPrimary}`} data-testid={`lead-run-title-${r.run_id}`}>{runTitle(r)}</p>
        <p className={`text-[11px] ${textSecondary} mt-0.5`} data-testid={`lead-run-status-${r.run_id}`}>
          {status} · {ready} ready{cancelled ? ` · ${cancelled} cancelled` : ''} · ${r.charged.toFixed(2)} charged · {new Date(r.created_at).toLocaleString()}
        </p>
        {r.error && <p className="text-[11px] text-red-400 mt-0.5">{r.error}</p>}
        {active && r.status !== 'queued' && <div className="h-1 mt-2 rounded-full overflow-hidden bg-white/5"><div className="h-full bg-gradient-to-r from-teal-400 to-violet-400 rounded-full transition-all" style={{ width: `${Math.max(3, 100 * (ready + failed) / Math.max(1, total))}%` }} /></div>}
      </div>
      <div className="w-full sm:w-auto pl-12 sm:pl-0 shrink-0 flex items-center justify-end gap-1">
        {!active && failed > 0 && <button onClick={retry} data-testid={`lead-run-retry-${r.run_id}`}
          className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg bg-violet-500/15 text-violet-300 font-semibold hover:bg-violet-500/25"><RotateCcw size={11} /> Retry {failed} failed</button>}
        {active && <button className={btn} disabled={r.cancel_requested} onClick={cancel} title="Cancel remaining" data-testid={`lead-run-cancel-${r.run_id}`}><Square size={14} /></button>}
        <button className={btn} onClick={download} title="Download receipt" data-testid={`lead-run-download-${r.run_id}`}><Download size={14} /></button>
        <button className={btn} onClick={toggle} title="Details" aria-expanded={open} data-testid={`lead-run-detail-${r.run_id}`}>{loading ? <Loader2 size={14} className="animate-spin" /> : <ChevronDown size={14} className={open ? 'rotate-180' : ''} />}</button>
        <span className="w-6 flex justify-center">
          {r.status === 'queued' ? <Clock size={16} className="text-amber-400" /> : active ? <Loader2 size={16} className="animate-spin text-teal-400" />
            : allFailed ? <XCircle size={16} className="text-red-400" /> : <CheckCircle2 size={16} className={failed ? 'text-amber-400' : 'text-emerald-400'} />}
        </span>
      </div>
    </div>
    {open && items && <div className="mt-3 ml-12 space-y-1.5" data-testid={`lead-run-items-${r.run_id}`}>
      {items.map(i => <div key={i.lead_id} className="flex items-start justify-between gap-3 text-[11px]" data-testid={`lead-run-item-${r.run_id}-${i.lead_id}`}>
        <div className="min-w-0"><span className={textPrimary}>{i.lead.name}</span>{i.error && <p className="text-red-400/90">{i.error}</p>}</div>
        <span className={`shrink-0 font-semibold ${i.status === 'ready' ? 'text-emerald-400' : i.status === 'failed' ? 'text-red-400' : textSecondary}`}>{i.status}</span>
      </div>)}
    </div>}
  </div>;
};
